import { createReducer } from 'redux-create-reducer';
import callAPI from '../../../utils/callAPI';
import { getOrCreateDeviceId } from '../../../utils/localStorage';

// FCM Initialization
export const INITIALIZE_FCM = 'INITIALIZE_FCM';
export const INITIALIZE_FCM_SUCCESS = 'INITIALIZE_FCM_SUCCESS';
export const INITIALIZE_FCM_FAILURE = 'INITIALIZE_FCM_FAILURE';

// Request Permission
export const REQUEST_FCM_PERMISSION = 'REQUEST_FCM_PERMISSION';
export const REQUEST_FCM_PERMISSION_SUCCESS = 'REQUEST_FCM_PERMISSION_SUCCESS';
export const REQUEST_FCM_PERMISSION_FAILURE = 'REQUEST_FCM_PERMISSION_FAILURE';

// Get FCM Token
export const GET_FCM_TOKEN = 'GET_FCM_TOKEN';
export const GET_FCM_TOKEN_SUCCESS = 'GET_FCM_TOKEN_SUCCESS';
export const GET_FCM_TOKEN_FAILURE = 'GET_FCM_TOKEN_FAILURE';

// Save FCM Token to Backend
export const ACTION_SAVE_FCM_TOKEN = 'ACTION_SAVE_FCM_TOKEN';
export const SAVE_FCM_TOKEN = 'SAVE_FCM_TOKEN';
export const SAVE_FCM_TOKEN_SUCCESS = 'SAVE_FCM_TOKEN_SUCCESS';
export const SAVE_FCM_TOKEN_FAILURE = 'SAVE_FCM_TOKEN_FAILURE';

// Delete FCM Token
export const ACTION_DELETE_FCM_TOKEN = 'ACTION_DELETE_FCM_TOKEN';
export const DELETE_FCM_TOKEN = 'DELETE_FCM_TOKEN';
export const DELETE_FCM_TOKEN_SUCCESS = 'DELETE_FCM_TOKEN_SUCCESS';
export const DELETE_FCM_TOKEN_FAILURE = 'DELETE_FCM_TOKEN_FAILURE';

// Handle FCM Message
export const FCM_MESSAGE_RECEIVED = 'FCM_MESSAGE_RECEIVED';
export const FCM_MESSAGE_RECEIVED_FOREGROUND = 'FCM_MESSAGE_RECEIVED_FOREGROUND';

// Reset FCM State
export const RESET_FCM_STATE = 'RESET_FCM_STATE';

const defaultState = {
  // Initialization
  isInitializing: false,
  isInitialized: false,
  initializationError: null,

  // Permission
  hasPermission: false,
  isRequestingPermission: false,
  permissionError: null,

  // Token
  fcmToken: null,
  isGettingToken: false,
  getTokenError: null,
  deviceId: null,

  // Save Token
  isSavingToken: false,
  saveTokenError: null,
  isTokenSaved: false,

  // Delete Token
  isDeletingToken: false,
  deleteTokenError: null,

  // Messages
  lastMessage: null,
  messageHistory: [],
};

const reducer = createReducer(defaultState, {
  // Initialize FCM
  [INITIALIZE_FCM]: (state) => ({
    ...state,
    isInitializing: true,
    isInitialized: false,
    initializationError: null,
  }),
  [INITIALIZE_FCM_SUCCESS]: (state, action) => ({
    ...state,
    isInitializing: false,
    isInitialized: true,
    fcmToken: action.payload?.token || state.fcmToken,
    hasPermission: action.payload?.hasPermission || state.hasPermission,
  }),
  [INITIALIZE_FCM_FAILURE]: (state, action) => ({
    ...state,
    isInitializing: false,
    isInitialized: false,
    initializationError: action.payload,
  }),

  // Request Permission
  [REQUEST_FCM_PERMISSION]: (state) => ({
    ...state,
    isRequestingPermission: true,
    permissionError: null,
  }),
  [REQUEST_FCM_PERMISSION_SUCCESS]: (state, action) => ({
    ...state,
    isRequestingPermission: false,
    hasPermission: action.payload || false,
  }),
  [REQUEST_FCM_PERMISSION_FAILURE]: (state, action) => ({
    ...state,
    isRequestingPermission: false,
    hasPermission: false,
    permissionError: action.payload,
  }),

  // Get Token
  [GET_FCM_TOKEN]: (state) => ({
    ...state,
    isGettingToken: true,
    getTokenError: null,
  }),
  [GET_FCM_TOKEN_SUCCESS]: (state, action) => ({
    ...state,
    isGettingToken: false,
    fcmToken: action.payload,
  }),
  [GET_FCM_TOKEN_FAILURE]: (state, action) => ({
    ...state,
    isGettingToken: false,
    fcmToken: null,
    getTokenError: action.payload,
  }),

  // Save Token (from API request)
  [SAVE_FCM_TOKEN]: (state) => ({
    ...state,
    isSavingToken: true,
    saveTokenError: null,
    isTokenSaved: false,
  }),
  [SAVE_FCM_TOKEN_SUCCESS]: (state) => {
    // Get deviceId from localStorage (it was generated/used in the API call)
    let deviceId = state.deviceId;
    if (typeof window !== 'undefined') {
      const storedDeviceId = localStorage.getItem('fcm-device-id');
      if (storedDeviceId) {
        deviceId = storedDeviceId;
      }
    }

    return {
      ...state,
      isSavingToken: false,
      isTokenSaved: true,
      deviceId: deviceId || state.deviceId,
    };
  },
  [SAVE_FCM_TOKEN_FAILURE]: (state, action) => ({
    ...state,
    isSavingToken: false,
    isTokenSaved: false,
    saveTokenError: action.payload,
  }),

  // Delete Token (from API request)
  [DELETE_FCM_TOKEN]: (state) => ({
    ...state,
    isDeletingToken: true,
    deleteTokenError: null,
  }),
  [DELETE_FCM_TOKEN_SUCCESS]: (state) => ({
    ...state,
    isDeletingToken: false,
    fcmToken: null,
    deviceId: null, // Clear from Redux state, but keep in localStorage for reuse
    isTokenSaved: false,
  }),
  [DELETE_FCM_TOKEN_FAILURE]: (state, action) => ({
    ...state,
    isDeletingToken: false,
    deleteTokenError: action.payload,
  }),

  // Message Received
  [FCM_MESSAGE_RECEIVED]: (state, action) => ({
    ...state,
    lastMessage: action.payload,
    messageHistory: [action.payload, ...state.messageHistory].slice(0, 50), // Keep last 50 messages
  }),
  [FCM_MESSAGE_RECEIVED_FOREGROUND]: (state, action) => ({
    ...state,
    lastMessage: action.payload,
    messageHistory: [action.payload, ...state.messageHistory].slice(0, 50), // Keep last 50 messages
  }),

  // Reset State
  [RESET_FCM_STATE]: () => defaultState,
});

export default reducer;
export const namespace = 'fcm';

// Action Creators
// VAPID key is automatically read from REACT_APP_FCM_VAPID_KEY env variable
export const initializeFCM = () => ({
  type: INITIALIZE_FCM,
});

export const requestFCMPermission = () => ({
  type: REQUEST_FCM_PERMISSION,
});

export const getFCMToken = () => ({
  type: GET_FCM_TOKEN,
});

export const saveFCMToken = (token) => ({
  type: ACTION_SAVE_FCM_TOKEN,
  payload: { token },
});

export const deleteFCMToken = () => ({
  type: ACTION_DELETE_FCM_TOKEN,
});

export const fcmMessageReceived = (message) => ({
  type: FCM_MESSAGE_RECEIVED,
  payload: message,
});

export const resetFCMState = () => ({
  type: RESET_FCM_STATE,
});

// API Functions
export const saveFCMTokenAPI = (token) => async (dispatch, getState) => {
  // Get or create device ID
  const deviceId = getOrCreateDeviceId();

  return await callAPI({
    method: 'post',
    apiPath: '/notification/register-device',
    actionTypes: [SAVE_FCM_TOKEN, SAVE_FCM_TOKEN_SUCCESS, SAVE_FCM_TOKEN_FAILURE],
    variables: {
      token,
      deviceId,
      platform: 'web',
    },
    dispatch,
    getState,
  });
};

/**
 * Delete FCM token from backend
 * Optimized to get deviceId from localStorage only (simplified approach)
 * @returns {Promise<Object>} Result object with success status
 */
export const deleteFCMTokenAPI = () => async (dispatch, getState) => {
  // Get deviceId from localStorage only (deviceId persists in localStorage)
  let deviceId = null;
  if (typeof window !== 'undefined') {
    deviceId = localStorage.getItem('fcm-device-id');
  }

  return await callAPI({
    method: 'delete',
    apiPath: `/notification/remove-device/${deviceId}`,
    actionTypes: [DELETE_FCM_TOKEN, DELETE_FCM_TOKEN_SUCCESS, DELETE_FCM_TOKEN_FAILURE],
    variables: {},
    dispatch,
    getState,
  });
};

// Selectors
export const selectFCMState = (state) => state.fcm;
export const selectFCMToken = (state) => state.fcm?.fcmToken;
export const selectFCMDeviceId = (state) => state.fcm?.deviceId;
export const selectHasFCMPermission = (state) => state.fcm?.hasPermission;
export const selectIsFCMInitialized = (state) => state.fcm?.isInitialized;
export const selectLastFCMMessage = (state) => state.fcm?.lastMessage;
export const selectFCMInitializationError = (state) => state.fcm?.initializationError;
